'use client';

import { Users, MapPin, Building2, Box } from 'lucide-react';
import type { BaseTileProps, SearchResult } from '../types';
import { cn } from '@/lib/utils';
import { ScoreBadge, SourceBadge } from './BaseTile';
import { activateOnKey } from '@/lib/a11y/activateOnKey';

const ENTITY_TYPE_CONFIG: Record<string, { icon: typeof Users; color: string; bg: string }> = {
  person: { icon: Users, color: 'text-violet-400', bg: 'bg-violet-500/15' },
  location: { icon: MapPin, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  organization: { icon: Building2, color: 'text-blue-400', bg: 'bg-blue-500/15' },
  object: { icon: Box, color: 'text-orange-400', bg: 'bg-orange-500/15' },
  vehicle: { icon: Box, color: 'text-amber-400', bg: 'bg-amber-500/15' },
};

const DEFAULT_CONFIG = { icon: Users, color: 'text-slate-400', bg: 'bg-slate-500/15' };

export function EntityTile({
  result,
  mode,
  showScore,
  showSource,
  highlighted,
  listboxOption,
  gridCell,
  onClick,
  className,
}: BaseTileProps<SearchResult>) {
  const meta = result.metadata as Record<string, unknown>;
  const entityType = (meta.entityType as string || 'unknown').toLowerCase();
  const config = ENTITY_TYPE_CONFIG[entityType] || DEFAULT_CONFIG;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
        highlighted ? 'bg-violet-500/15' : 'hover:bg-slate-800/50',
        className
      )}
      id={gridCell?.id ?? listboxOption?.id}
      role={gridCell ? 'gridcell' : listboxOption ? 'option' : 'button'}
      aria-selected={gridCell?.selected ?? listboxOption?.selected}
      tabIndex={listboxOption || gridCell ? -1 : 0}
      onClick={onClick}
      onKeyDown={activateOnKey(onClick)}
    >
      <div className="flex items-start gap-2.5">
        <div className={cn('w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5', config.bg)}>
          <Icon className={cn('w-3.5 h-3.5', config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm text-white truncate">{result.title}</h4>
            <span className={cn(
              'px-1.5 py-0.5 text-[10px] font-medium rounded-full border whitespace-nowrap capitalize',
              config.color,
              config.bg,
              `border-current/20`
            )}>
              {entityType}
            </span>
            {showScore && result.score > 0 && <ScoreBadge score={result.score} />}
            {showSource && <SourceBadge source={result.source} />}
          </div>

          {result.subtitle && (
            <p className="text-xs text-slate-500 mt-0.5 truncate">{result.subtitle}</p>
          )}

          {result.preview && mode === 'expanded' && (
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{result.preview}</p>
          )}
        </div>
      </div>
    </div>
  );
}
